import React, { useEffect, useState } from "react";
import { 
  collection, 
  getDocs, 
  getDoc, 
  addDoc, 
  writeBatch, 
  doc, 
  updateDoc, 
  query, 
  where 
} from "firebase/firestore";
import { db } from "../firebase";
import "../Style.css";
import "../index.css";
import "../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "./SalaryProcess.css";

export default function SalaryProcess({ initialMonth, initialStatusFilter, initialEmployeeFilter }) {
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  const [selectedMonth, setSelectedMonth] = useState(
    initialMonth || `${now.getFullYear()}-${pad(now.getMonth() + 1)}`
  );
  const [result, setResult] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [employeesList, setEmployeesList] = useState([]);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [otHourlyRate, setOtHourlyRate] = useState(65);
  const [toasts, setToasts] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [actionLoading, setActionLoading] = useState({}); // { [empId]: 'save' | 'pay' | 'partial' }
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [showSlip, setShowSlip] = useState(null); // row for slip modal
  const [showOtDetail, setShowOtDetail] = useState(false); // show OT breakdown inside slip
  const [expandedOtEmpId, setExpandedOtEmpId] = useState(null); // which row's OT is expanded
  const [expandedHistoryEmpId, setExpandedHistoryEmpId] = useState(null); // which row's payment history is expanded

  const showToast = (message, type = "success", timeout = 4000) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, timeout);
  };

  const fetchGlobalSettings = async () => {
    try {
      const docRef = doc(db, "settings", "global");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setGlobalSettings(data);
        // Always read working hours and OT rate from settings - do not use manual input
        if (data.workingHours) setHoursPerDay(data.workingHours);
        if (data.otHourlyRate) setOtHourlyRate(data.otHourlyRate);
      }
    } catch (e) {
      console.error("Error fetching global settings:", e);
    }
  };

  const processSalary = async () => {
    setProcessing(true);
    try {
      const year = +selectedMonth.slice(0, 4);
      const month = +selectedMonth.slice(5, 7);

      const empSnap = await getDocs(collection(db, "EMPLOYEES"));
      const allEmployees = empSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      const employees = allEmployees.filter(emp => emp.category !== "Contract");
      setEmployeesList(employees);

      const attSnap = await getDocs(collection(db, "attendance"));
      const attendance = attSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // fetch advances for this year, then filter by month client-side
      let advances = [];
      try {
        const advQ = query(collection(db, "advance"), where("year", "==", year));
        const advSnap = await getDocs(advQ);
        advances = advSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        // filter to requested month, handling numeric or string month storage and monthName
        const monthNameLower = new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(year, month - 1)).toLowerCase();
        advances = advances.filter(a => {
          if (!a) return false;
          // if an advance is debited and already linked to a salary record, ignore it
          if (a.status === 'debited' && a.salaryId) return false;
          if (a.month === month || a.month === Number(month) || String(a.month) === String(month)) return true;
          if (a.monthName && String(a.monthName).toLowerCase() === monthNameLower) return true;
          return false;
        });
        // debug: log advances loaded for this month/year
        try { console.debug('SalaryProcess: advances for', month, year, advances); } catch (e) { }
      } catch (e) {
        console.warn('Could not load advances', e);
      }

      const daysInMonth = new Date(year, month, 0).getDate();

      // Fetch settings directly to avoid stale React state (hoursPerDay/otHourlyRate state may not have updated yet)
      let effectiveHoursPerDay = hoursPerDay || 8;
      let effectiveOtHourlyRate = otHourlyRate || 65;
      let effectiveOtEnabled = true;
      try {
        const settingsSnap = await getDoc(doc(db, "settings", "global"));
        if (settingsSnap.exists()) {
          const s = settingsSnap.data();
          effectiveHoursPerDay = Number(s.workingHours) || effectiveHoursPerDay;
          effectiveOtHourlyRate = Number(s.otHourlyRate) || effectiveOtHourlyRate;
          effectiveOtEnabled = s.otEnabled !== false;
          // Also sync state for display
          setGlobalSettings(s);
          setHoursPerDay(effectiveHoursPerDay);
          setOtHourlyRate(effectiveOtHourlyRate);
        }
      } catch (e) {
        console.warn('Could not reload settings in processSalary:', e);
      }

      // helpers to compute hours and OT (copied logic from Attendance.js)
      const parseHoursToMinutes = (h) => {
        if (!h) return null;
        if (typeof h === "number") return Math.round(h * 60);
        const s = String(h).trim();
        if (s.includes(":")) {
          const [hh, mm] = s.split(":");
          const H = Number(hh) || 0;
          const M = Number(mm) || 0;
          return H * 60 + M;
        }
        const n = s.replace(/h$/, "").trim();
        const f = Number(n);
        if (!Number.isFinite(f)) return null;
        return Math.round(f * 60);
      };

      const computeRowHours = (r) => {
        let ci = null;
        let co = null;
        if (r.checkInTime && typeof r.checkInTime.toDate === "function") ci = r.checkInTime.toDate();
        if (r.checkOutTime && typeof r.checkOutTime.toDate === "function") co = r.checkOutTime.toDate();

        let mins = null;
        if (ci && co) {
          mins = Math.max(0, Math.round((co - ci) / 60000));
        } else if (r.hours) {
          mins = parseHoursToMinutes(r.hours);
        }

        if (mins === null) return { hours: null, ot: 0 };

        const hours = mins / 60;
        const ot = Math.max(0, hours - 8);
        return { hours, ot };
      };

      const otMultiplier = 1.5; // OT pay = perHour * otHours * multiplier

      const salaryData = employees.map((emp) => {
        const empAttendance = attendance.filter(
          (a) =>
            (a.empId === emp.uid || a.empId === emp.empId) &&
            (a.checkInTime?.toDate ? a.checkInTime.toDate().getMonth() + 1 === month : true) &&
            (a.checkInTime?.toDate ? a.checkInTime.toDate().getFullYear() === year : (a.year ? a.year === year : true))
        );

        // Calculate net present days and OT
        let totalNetDays = 0;
        let totalOtHours = 0;
        const otDetails = []; // per-day OT breakdown
        for (const a of empAttendance) {
          const { hours } = computeRowHours(a);

          let status = a.status;
          if (!status) {
            if (hours === null || hours < 4.5) status = "A";
            else if (hours >= 4.5 && hours < 5.5) status = "H";
            else status = "P";
          }

          // Re-calculate OT based on settings fetched directly (not stale state)
          const otThreshold = effectiveHoursPerDay;
          const otEnabled = effectiveOtEnabled;
          const actualOt = (otEnabled && status !== "A" && hours !== null) ? Math.max(0, hours - otThreshold) : 0;
          totalOtHours += actualOt;

          // Collect per-day OT detail if OT > 0
          if (actualOt > 0) {
            let ciStr = "-";
            let coStr = "-";
            try {
              if (a.checkInTime?.toDate) ciStr = a.checkInTime.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              if (a.checkOutTime?.toDate) coStr = a.checkOutTime.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } catch (e) { }
            otDetails.push({ date: a.date || "", checkIn: ciStr, checkOut: coStr, otHours: Number(actualOt.toFixed(2)) });
          }

          if (status === "P") totalNetDays += 1;
          else if (status === "H") totalNetDays += 0.5;
        }

        const presentDays = totalNetDays;
        // Per-day salary should be based on actual days in the selected month
        const monthName = new Intl.DateTimeFormat(undefined, { month: "long" }).format(
          new Date(year, month - 1)
        );

        // Sundays/holidays are only counted if attendance is recorded (already in presentDays)
        // If no attendance on Sunday/holiday, it's treated as absent
        const totalPayableDays = presentDays;
        const perDay = emp.monthlySalary / daysInMonth;
        const earned = perDay * totalPayableDays;

        const otPay = Math.round(totalOtHours * effectiveOtHourlyRate);

        // apply advance (only non-debited advances)
        const empAdv = advances.filter(a => (a.empId === emp.uid) && a.status !== 'debited');
        // debug: show matched advances for this employee
        try { console.debug('SalaryProcess: matched advances for', emp.uid, emp.name, empAdv); } catch (e) { }
        const advanceAmount = empAdv.reduce((s, x) => s + (Number(x.amount) || 0), 0);
        const advanceIds = empAdv.map(x => x.id);

        const gross = Math.round(earned) + otPay;
        const net = Math.max(0, gross - advanceAmount);

        return {
          empId: emp.uid,
          name: emp.name,
          month,
          monthName,
          year,
          presentDays,
          basicSalary: emp.monthlySalary,
          advanceAmount,
          advances: advanceIds,
          otHours: Number(totalOtHours.toFixed(2)),
          otPay,
          otDetails,
          notes: "",
          grossSalary: gross,
          netSalary: net,
          saved: false,
          status: "new",
          docId: null,
          savedAt: null,
        };
      });

      // load any existing saved salary documents for this month/year
      try {
        const q = query(collection(db, "salary"), where("year", "==", year), where("month", "==", month));
        const snap = await getDocs(q);
        if (!snap.empty) {
          // build map of latest document per empId
          const map = {};
          snap.docs.forEach((d) => {
            const data = d.data();
            const empId = data.empId || data.empid || data.empID || data.emp;
            if (!empId) return;
            const existing = map[empId];
            const ts = data.savedAt ? (data.savedAt.toDate ? data.savedAt.toDate().getTime() : new Date(data.savedAt).getTime()) : (data.generatedAt ? (data.generatedAt.toDate ? data.generatedAt.toDate().getTime() : new Date(data.generatedAt).getTime()) : 0);
            if (!existing || ts > existing.ts) {
              map[empId] = { id: d.id, data, ts };
            }
          });

          const merged = salaryData.map((r) => {
            const found = map[r.empId];
            if (!found) return r;
            const d = found.data;
            return {
              ...r,
              saved: true,
              status: d.status || "pending",
              docId: found.id,
              savedAt: d.savedAt || d.generatedAt || null,
              paidAt: d.paidAt || null,
              paidAmount: Number(d.paidAmount) || 0,
              paymentHistory: d.paymentHistory || [],
              notes: d.notes || "",
              netSalary: Number(d.netSalary) || r.netSalary,
              grossSalary: Number(d.grossSalary) || r.grossSalary,
            };
          });

          setResult(merged);
        } else {
          setResult(salaryData);
        }
      } catch (err) {
        console.error("Error loading existing salaries:", err);
        setResult(salaryData);
      }
    } catch (err) {
      console.error("Error in processSalary:", err);
      showToast("Failed to process salary", "danger");
    } finally {
      setProcessing(false);
    }
  };

  // auto-run when selectedMonth changes so UI reflects saved status after reload
  const handleRowChange = (empId, field, value) => {
    setResult(prev => prev.map(r => {
      if (r.empId === empId) {
        return { ...r, [field]: value };
      }
      return r;
    }));
  };

  useEffect(() => {
    const init = async () => {
      await fetchGlobalSettings();
      processSalary();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  useEffect(() => {
    if (initialMonth) setSelectedMonth(initialMonth);
  }, [initialMonth]);

  useEffect(() => {
    if (initialStatusFilter) setStatusFilter(initialStatusFilter);
    if (initialEmployeeFilter) setEmployeeFilter(initialEmployeeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStatusFilter, initialEmployeeFilter]);

  const saveSalary = async (row) => {
    try {
      setActionLoading(prev => ({ ...prev, [row.empId]: 'save' }));
      const now = new Date();
      const docRef = await addDoc(collection(db, "salary"), {
        ...row,
        generatedAt: now,
        savedAt: now,
        monthName: row.monthName || new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(row.year, row.month - 1)),
        status: "pending",
      });

      // mark applied advances as debited and link to salary
      if (row.advances && row.advances.length) {
        try {
          await Promise.all(row.advances.map(aid => updateDoc(doc(db, "advance", aid), { status: "debited", debitedAt: now, salaryId: docRef.id })));
        } catch (e) {
          console.warn('Failed to update advances for salary', e);
        }
      }

      setResult((prev) =>
        prev.map((r) =>
          r.empId === row.empId
            ? { ...r, saved: true, status: "pending", docId: docRef.id, savedAt: now }
            : r
        )
      );
      showToast(`Salary saved for ${row.name}`, "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to save salary", "danger");
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[row.empId]; return n; });
    }
  };

  const saveAll = async () => {
    if (!result || result.length === 0) {
      showToast("No salary data to save", "danger");
      return;
    }

    try {
      setSavingAll(true);
      const batch = writeBatch(db);
      const colRef = collection(db, "salary");
      const refs = [];

      result.forEach((r) => {
        const ref = doc(colRef);
        refs.push({ empId: r.empId, id: ref.id });
        const now = new Date();
        batch.set(ref, {
          ...r,
          generatedAt: now,
          savedAt: now,
          monthName: r.monthName || new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(r.year, r.month - 1)),
          status: "pending",
        });
        // update advance docs in the same batch (mark debited)
        if (r.advances && r.advances.length) {
          r.advances.forEach(aid => {
            const advRef = doc(db, "advance", aid);
            batch.update(advRef, { status: "debited", debitedAt: now, salaryId: ref.id });
          });
        }
      });

      await batch.commit();
      setResult((prev) =>
        prev.map((r) => {
          const f = refs.find((x) => x.empId === r.empId);
          return f
            ? { ...r, saved: true, status: "pending", docId: f.id, savedAt: new Date() }
            : r;
        })
      );
      showToast("All salaries saved successfully", "success");
    } catch (err) {
      console.error("Error saving salaries:", err);
      showToast("Failed to save salaries. Check console.", "danger");
    } finally {
      setSavingAll(false);
    }
  };

  const addCashbookEntry = async (amount, empName, monthName, type) => {
    try {
      await addDoc(collection(db, "CASHBOOK"), {
        date: new Date(),
        description: `Salary ${type} - ${monthName} - ${empName}`,
        category: "Salary Expense",
        income: 0,
        expense: amount,
        paymentMode: "Cash", // Default or could be passed
        createdAt: new Date()
      });
      console.log(`✅ Cashbook entry created for ${empName}`);
    } catch (err) {
      console.error("❌ Failed to create cashbook entry:", err);
    }
  };

  const markPaid = async (row) => {
    try {
      setActionLoading(prev => ({ ...prev, [row.empId]: 'pay' }));
      if (!row.docId) {
        showToast("Missing salary record id", "danger");
        return;
      }

      const netSalary = Number(row.netSalary) || 0;
      const alreadyPaid = Number(row.paidAmount) || 0;
      const amountToPay = netSalary - alreadyPaid;

      if (amountToPay <= 0) {
        showToast("Already fully paid", "warning");
        return;
      }

      const ref = doc(db, "salary", row.docId);
      const now = new Date();

      const newHistory = [
        ...(row.paymentHistory || []),
        { amount: amountToPay, date: now, type: 'full_settlement' }
      ];

      await updateDoc(ref, {
        status: "paid",
        paidAt: now,
        paidAmount: netSalary,
        paidAmount: netSalary,
        paymentHistory: newHistory,
        notes: row.notes // Update notes on full payment
      });

      // Integrated Cashbook Entry
      // await addCashbookEntry(amountToPay, row.name, row.monthName, "Full Payment");

      setResult((prev) => prev.map((r) =>
      (r.empId === row.empId ? {
        ...r,
        status: "paid",
        paidAt: now,
        paidAmount: netSalary,
        paymentHistory: newHistory
      } : r)
      ));

      showToast(`Marked paid and cashbook updated for ${row.name}`, "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to mark paid", "danger");
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[row.empId]; return n; });
    }
  };

  const markPartial = async (row) => {
    try {
      if (!row.docId) {
        showToast("Missing salary record id", "danger");
        return;
      }

      const netSalary = Number(row.netSalary) || 0;
      const alreadyPaid = Number(row.paidAmount) || 0;
      const remaining = netSalary - alreadyPaid;

      const input = window.prompt(`Enter partial amount (Remaining: ${formatCurrency(remaining)})`);
      if (!input) return; // cancelled

      setActionLoading(prev => ({ ...prev, [row.empId]: 'partial' }));

      const amount = Number(input);
      if (isNaN(amount) || amount <= 0) {
        showToast("Invalid amount", "danger");
        setActionLoading(prev => { const n = { ...prev }; delete n[row.empId]; return n; });
        return;
      }

      if (amount > remaining) {
        showToast(`Amount exceeds remaining balance (${formatCurrency(remaining)})`, "danger");
        setActionLoading(prev => { const n = { ...prev }; delete n[row.empId]; return n; });
        return;
      }

      const newPaidTotal = alreadyPaid + amount;
      const isFullyPaid = newPaidTotal >= netSalary;
      const newStatus = isFullyPaid ? "paid" : "partial";
      const now = new Date();

      const newHistory = [
        ...(row.paymentHistory || []),
        { amount: amount, date: now, type: 'partial' }
      ];

      const ref = doc(db, "salary", row.docId);
      const updates = {
        paidAmount: newPaidTotal,
        paymentHistory: newHistory,
        paymentHistory: newHistory,
        status: newStatus,
        notes: row.notes // Update notes on partial payment
      };

      if (isFullyPaid) updates.paidAt = now;

      await updateDoc(ref, updates);

      // Integrated Cashbook Entry for partial payment
      // await addCashbookEntry(amount, row.name, row.monthName, "Partial Payment");

      setResult((prev) => prev.map((r) =>
      (r.empId === row.empId ? {
        ...r,
        ...updates
      } : r)
      ));

      showToast(`Partial payment recorded and cashbook updated for ${row.name}`, "success");

    } catch (err) {
      console.error(err);
      showToast("Failed to record partial payment", "danger");
    } finally {
      setActionLoading(prev => { const n = { ...prev }; delete n[row.empId]; return n; });
    }
  };

  const editPartialEntry = async (row, entryIndex) => {
    try {
      if (!row.docId) { showToast("Missing salary record id", "danger"); return; }
      const entry = (row.paymentHistory || [])[entryIndex];
      if (!entry) return;
      const input = window.prompt(`Edit amount for this payment (current: ₹${formatCurrency(entry.amount)}):`, entry.amount);
      if (input === null) return; // cancelled
      const newAmount = Number(input);
      if (isNaN(newAmount) || newAmount <= 0) { showToast("Invalid amount", "danger"); return; }

      const newHistory = (row.paymentHistory || []).map((e, i) => i === entryIndex ? { ...e, amount: newAmount } : e);
      const newPaidTotal = newHistory.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const isFullyPaid = newPaidTotal >= (Number(row.netSalary) || 0);
      const newStatus = isFullyPaid ? "paid" : "partial";

      const updates = { paymentHistory: newHistory, paidAmount: newPaidTotal, status: newStatus };
      await updateDoc(doc(db, "salary", row.docId), updates);
      setResult(prev => prev.map(r => r.empId === row.empId ? { ...r, ...updates } : r));
      showToast("Payment entry updated", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to edit payment", "danger");
    }
  };

  const deletePartialEntry = async (row, entryIndex) => {
    try {
      if (!row.docId) { showToast("Missing salary record id", "danger"); return; }
      if (!window.confirm("Delete this payment entry?")) return;

      const newHistory = (row.paymentHistory || []).filter((_, i) => i !== entryIndex);
      const newPaidTotal = newHistory.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const newStatus = newPaidTotal <= 0 ? "pending" : (newPaidTotal >= (Number(row.netSalary) || 0) ? "paid" : "partial");

      const updates = { paymentHistory: newHistory, paidAmount: newPaidTotal, status: newStatus };
      await updateDoc(doc(db, "salary", row.docId), updates);
      setResult(prev => prev.map(r => r.empId === row.empId ? { ...r, ...updates } : r));
      showToast("Payment entry deleted", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to delete payment", "danger");
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat(undefined).format(v);
  const formatDate = (d) => {
    if (!d) return "-";
    try {
      const dt = d instanceof Date ? d : d.toDate ? d.toDate() : new Date(d);
      return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(dt);
    } catch (e) {
      return String(d);
    }
  };

  // apply top-level filters to result
  const filteredResult = result.filter(r => {
    if (employeeFilter && r.empId !== employeeFilter) return false;
    if (statusFilter && statusFilter !== 'All' && r.status !== statusFilter) return false;
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredResult.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredResult.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="salary-card">
      {/* Toast container */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast show mb-2 ${t.type === "success" ? "bg-success text-white" : "bg-danger text-white"}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">{t.message}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setToasts((s) => s.filter((x) => x.id !== t.id))}
              ></button>
            </div>
          </div>
        ))}
      </div>
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex align-items-center mb-3">
            <div className="me-auto">
              <h5 className="card-title mb-0">Salary Process</h5>
              <small className="text-muted">Generate and save monthly payroll</small>
            </div>

            <div className="d-flex gap-2">
              <input
                type="month"
                className="form-control form-control-sm"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
              <button className="btn btn-primary btn-sm" onClick={processSalary} disabled={processing}>
                {processing ? <span className="spinner-border spinner-border-sm" /> : 'Process'}
              </button>
              <button className="btn btn-outline-success btn-sm" onClick={saveAll} disabled={savingAll}>
                {savingAll ? <span className="spinner-border spinner-border-sm" /> : 'Save All'}
              </button>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card bg-light border-0 shadow-sm text-center p-3">
                <small className="text-muted d-block mb-1">Total Net Salary</small>
                <h4 className="fw-bold mb-0">₹ {formatCurrency(filteredResult.reduce((s, r) => s + (Number(r.netSalary) || 0), 0))}</h4>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success bg-opacity-10 border-0 shadow-sm text-center p-3">
                <small className="text-success d-block mb-1">Total Paid</small>
                <h4 className="fw-bold text-success mb-0">₹ {formatCurrency(filteredResult.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0))}</h4>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-danger bg-opacity-10 border-0 shadow-sm text-center p-3">
                <small className="text-danger d-block mb-1">Total Pending</small>
                <h4 className="fw-bold text-danger mb-0">₹ {formatCurrency(filteredResult.reduce((s, r) => s + (Math.max(0, (Number(r.netSalary) || 0) - (Number(r.paidAmount) || 0))), 0))}</h4>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-info bg-opacity-10 border-0 shadow-sm text-center p-3">
                <small className="text-info d-block mb-1">Processed Count</small>
                <h4 className="fw-bold text-info mb-0">{filteredResult.length}</h4>
              </div>
            </div>
          </div>

          <div className="card mb-3 p-2 bg-light border-0">
            <div className="row g-2 align-items-center">
              <div className="col-md-4">
                <select className="form-select form-select-sm" value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}>
                  <option value="">All employees</option>
                  {employeesList.map(emp => (
                    <option key={emp.uid || emp.id} value={emp.uid || emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="All">All status</option>
                  <option value="new">New</option>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>


              <div className="col-md-auto">
                <small className="text-muted">Working Hrs: <strong>{hoursPerDay}h/day</strong> &nbsp;|&nbsp; OT Rate: <strong>₹{otHourlyRate}/hr</strong> &nbsp;<span className="text-info" style={{fontSize:'0.75rem'}}>(from Attendance Settings)</span></small>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Month</th>
                  <th className="text-center">Present</th>
                  <th className="text-end">Basic</th>
                  <th className="text-center">OT Hrs</th>
                  <th className="text-end">OT Pay</th>
                  <th className="text-end">Advance</th>
                  <th className="text-end" style={{ width: '110px' }}>Net Salary</th>
                  <th style={{ width: '160px' }}>Notes</th>
                  <th className="text-end" style={{ width: '100px' }}>Saved On</th>
                  <th className="text-center" style={{ width: '180px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {result.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-4">
                      No data. Click Process to generate salaries.
                    </td>
                  </tr>
                )}

                {currentItems.map((r) => (
                  <tr key={r.empId} className={r.status === "paid" ? "table-success" : ""}>
                    <td>
                      <div className="fw-bold" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{r.name}</div>
                    </td>
                    <td><span className="badge bg-light text-dark">{r.monthName || "-"}</span></td>
                    <td className="text-center">{r.presentDays}</td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>₹ {formatCurrency(r.basicSalary)}</td>
                    <td className="text-center" style={{ position: 'relative' }}>
                      <span
                        className={r.otDetails && r.otDetails.length > 0 ? 'text-primary' : ''}
                        style={{ cursor: r.otDetails && r.otDetails.length > 0 ? 'pointer' : 'default', textDecoration: r.otDetails && r.otDetails.length > 0 ? 'underline' : 'none' }}
                        title={r.otDetails && r.otDetails.length > 0 ? 'Click to see OT breakdown' : ''}
                        onClick={() => { if (r.otDetails && r.otDetails.length > 0) setExpandedOtEmpId(prev => prev === r.empId ? null : r.empId); }}
                      >
                        {r.otHours || 0} {r.otDetails && r.otDetails.length > 0 ? (expandedOtEmpId === r.empId ? '▲' : '▼') : ''}
                      </span>
                      {expandedOtEmpId === r.empId && r.otDetails && r.otDetails.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, minWidth: '320px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px' }} onClick={e => e.stopPropagation()}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="fw-bold text-dark">OT Breakdown</small>
                            <button type="button" className="btn-close btn-close-sm" onClick={() => setExpandedOtEmpId(null)} />
                          </div>
                          <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.78rem' }}>
                            <thead className="table-light">
                              <tr><th>Date</th><th>Check In</th><th>Check Out</th><th>OT Hrs</th></tr>
                            </thead>
                            <tbody>
                              {r.otDetails.map((d, i) => (
                                <tr key={i}>
                                  <td>{d.date}</td>
                                  <td>{d.checkIn}</td>
                                  <td>{d.checkOut}</td>
                                  <td className="text-end fw-bold">{d.otHours}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>₹ {formatCurrency(r.otPay || 0)}</td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>₹ {formatCurrency(r.advanceAmount || 0)}</td>
                    <td className="text-end fw-bold" style={{ whiteSpace: 'nowrap' }}>₹ {formatCurrency(r.netSalary)}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Note..."
                        value={r.notes}
                        onChange={(e) => handleRowChange(r.empId, 'notes', e.target.value)}
                      />
                    </td>
                    <td className="text-end small" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.savedAt)}</td>
                    <td className="text-center">
                      <div className="d-flex flex-wrap gap-1 justify-content-center">
                        <button className="btn btn-sm btn-outline-info px-2" onClick={() => setShowSlip(r)} title="View Slip">📜</button>

                        {!r.saved && (
                          <button
                            className="btn btn-sm btn-outline-success px-2"
                            onClick={() => saveSalary(r)}
                            disabled={!!actionLoading[r.empId]}
                          >
                            {actionLoading[r.empId] === 'save' ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                          </button>
                        )}

                        {r.saved && (r.status === "pending" || r.status === "partial") && (
                          <>
                            <button className="btn btn-sm btn-success px-2" onClick={() => markPaid(r)} title="Pay Remaining" disabled={!!actionLoading[r.empId]}>
                              {actionLoading[r.empId] === 'pay' ? <span className="spinner-border spinner-border-sm" /> : 'Full'}
                            </button>
                            <button className="btn btn-sm btn-outline-primary px-2" onClick={() => markPartial(r)} title="Pay Partial" disabled={!!actionLoading[r.empId]}>
                              {actionLoading[r.empId] === 'partial' ? <span className="spinner-border spinner-border-sm" /> : 'Part'}
                            </button>
                          </>
                        )}

                        {r.status === "paid" && <span className="badge bg-success d-flex align-items-center">Paid</span>}
                      </div>

                      {(r.status === "partial" || (r.status === "paid" && r.paymentHistory && r.paymentHistory.length > 0)) && (
                        <div className="mt-1">
                          <small
                            className="text-primary d-block"
                            style={{ fontSize: '0.68rem', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => setExpandedHistoryEmpId(prev => prev === r.empId ? null : r.empId)}
                          >
                            Paid: ₹{formatCurrency(r.paidAmount || 0)} | Bal: ₹{formatCurrency(Math.max(0, (r.netSalary || 0) - (r.paidAmount || 0)))} {expandedHistoryEmpId === r.empId ? '▲' : '▼'}
                          </small>
                          {expandedHistoryEmpId === r.empId && (
                            <div className="border rounded p-1 mt-1 bg-white shadow-sm" style={{ fontSize: '0.72rem', minWidth: '280px', zIndex: 999, position: 'relative' }}>
                              <div className="fw-bold mb-1 text-dark" style={{ fontSize: '0.73rem' }}>Payment History</div>
                              {(r.paymentHistory || []).length === 0 && <div className="text-muted">No payments yet.</div>}
                              {(r.paymentHistory || []).map((h, i) => (
                                <div key={i} className="d-flex align-items-center gap-1 mb-1">
                                  <span className="flex-grow-1">
                                    {formatDate(h.date)} &nbsp;
                                    <span className={`badge ${h.type === 'full_settlement' ? 'bg-success' : 'bg-primary'} me-1`} style={{ fontSize: '0.6rem' }}>{h.type === 'full_settlement' ? 'Full' : 'Part'}</span>
                                    <strong>₹{formatCurrency(h.amount)}</strong>
                                  </span>
                                  {h.type !== 'full_settlement' && (
                                    <>
                                      <button className="btn btn-outline-warning btn-sm py-0 px-1" style={{ fontSize: '0.65rem' }} title="Edit" onClick={() => editPartialEntry(r, i)}>✏️</button>
                                      <button className="btn btn-outline-danger btn-sm py-0 px-1" style={{ fontSize: '0.65rem' }} title="Delete" onClick={() => deletePartialEntry(r, i)}>🗑️</button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredResult.length > 0 && (
            <div className={"d-flex justify-content-between align-items-center mt-3"}>
              <div className="d-flex align-items-center gap-2">
                <span>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>entries</span>
                <span className="ms-2 text-muted small">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredResult.length)} of {filteredResult.length}
                </span>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
                  </li>
                  {(() => {
                    const totalPages = Math.ceil(filteredResult.length / itemsPerPage);
                    return [...Array(totalPages)].map((_, i) => {
                      const p = i + 1;
                      if (
                        p === 1 ||
                        p === totalPages ||
                        (p >= currentPage - 2 && p <= currentPage + 2)
                      ) {
                        return (
                          <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                            <button className="page-link" onClick={() => paginate(p)}>{p}</button>
                          </li>
                        );
                      } else if (
                        (p === currentPage - 3 && currentPage > 4) ||
                        (p === currentPage + 3 && currentPage < totalPages - 3)
                      ) {
                        return <li key={p} className="page-item disabled"><span className="page-link">...</span></li>;
                      }
                      return null;
                    });
                  })()}
                  <li className={`page-item ${currentPage === Math.ceil(filteredResult.length / itemsPerPage) ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {showSlip && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Salary Slip - {showSlip.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowSlip(null)}></button>
              </div>
              <div className="modal-body p-4" id="salary-slip-content" onClick={() => setShowOtDetail(false)}>
                <div className="text-center mb-4">
                  <h3 className="fw-bold">ASIAN CLIMATE CONTROLS</h3>
                  <p className="text-muted">Salary Statement for {showSlip.monthName} {showSlip.year}</p>
                </div>
                <hr />
                <div className="row mb-4">
                  <div className="col-6">
                    <p className="mb-1"><strong>Employee Name:</strong> {showSlip.name}</p>
                    <p className="mb-0"><strong>Employee ID:</strong> {showSlip.empId}</p>
                  </div>
                  <div className="col-6 text-end">
                    <p className="mb-1"><strong>Status:</strong> <span className="badge bg-secondary">{showSlip.status.toUpperCase()}</span></p>
                    <p className="mb-0"><strong>Generated On:</strong> {formatDate(showSlip.savedAt || new Date())}</p>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Earnings</th>
                        <th className="text-end">Amount (₹)</th>
                        <th>Deductions</th>
                        <th className="text-end">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Basic Salary (Earned)</td>
                        <td className="text-end">{formatCurrency(showSlip.grossSalary != null ? showSlip.grossSalary - (showSlip.otPay || 0) : Math.round((showSlip.basicSalary / new Date(showSlip.year, showSlip.month, 0).getDate()) * showSlip.presentDays))}</td>
                        <td>Advance Deduction</td>
                        <td className="text-end">{formatCurrency(showSlip.advanceAmount || 0)}</td>
                      </tr>
                      <tr>
                        <td>
                          <span
                            className="text-primary"
                            style={{ cursor: (showSlip.otDetails && showSlip.otDetails.length > 0) ? 'pointer' : 'default', textDecoration: (showSlip.otDetails && showSlip.otDetails.length > 0) ? 'underline' : 'none' }}
                            title={(showSlip.otDetails && showSlip.otDetails.length > 0) ? 'Click to view OT details' : ''}
                            onClick={(e) => { e.stopPropagation(); if (showSlip.otDetails && showSlip.otDetails.length > 0) setShowOtDetail(v => !v); }}
                          >
                            OT Pay ({showSlip.otHours || 0} hrs) {(showSlip.otDetails && showSlip.otDetails.length > 0) ? (showOtDetail ? '▲' : '▼') : ''}
                          </span>
                          {showOtDetail && showSlip.otDetails && showSlip.otDetails.length > 0 && (
                            <div className="mt-2 p-2 border rounded bg-light" style={{ fontSize: '0.8rem' }} onClick={e => e.stopPropagation()}>
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Check In</th>
                                    <th>Check Out</th>
                                    <th className="text-end">OT Hrs</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {showSlip.otDetails.map((d, i) => (
                                    <tr key={i}>
                                      <td>{d.date}</td>
                                      <td>{d.checkIn}</td>
                                      <td>{d.checkOut}</td>
                                      <td className="text-end">{d.otHours}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                        <td className="text-end">{formatCurrency(showSlip.otPay || 0)}</td>
                        <td>Total Deductions</td>
                        <td className="text-end">{formatCurrency(showSlip.advanceAmount || 0)}</td>
                      </tr>
                    </tbody>
                    <tfoot className="table-dark">
                      <tr>
                        <th colSpan="3">NET SALARY PAYABLE</th>
                        <th className="text-end">₹ {formatCurrency(showSlip.netSalary)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {showSlip.paymentHistory && showSlip.paymentHistory.length > 0 && (
                  <div className="mt-4">
                    <h6>Payment History</h6>
                    <ul className="list-group list-group-flush small">
                      {showSlip.paymentHistory.map((h, i) => (
                        <li key={i} className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <span>{formatDate(h.date)} - {h.type === 'partial' ? 'Partial' : 'Full Settlement'}</span>
                          <span className="fw-bold text-success">₹ {formatCurrency(h.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {showSlip.notes && (
                  <div className="mt-3 p-2 bg-light rounded italic">
                    <small><strong>Notes:</strong> {showSlip.notes}</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSlip(null)}>Close</button>
                <button className="btn btn-primary" onClick={() => window.print()}>Print / Download</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
